/**
 * Pure helpers and class maps for conversation message parts (interaction requests,
 * progress rows). Split out of ConversationMessageParts.tsx to keep files under 500 lines.
 */
import {
  type ConversationInteractionAction,
  type ConversationInteractionField,
  type ConversationInteractionRequest,
  type ConversationInteractionSubmission,
  type ConversationMessagePart,
  validateConversationInteractionSubmission,
} from '@confluent/shared/lib/conversation-interactions';

export const emphasisClass: Record<string, string> = {
  normal: 'text-body',
  strong: 'font-semibold text-heading',
  muted: 'text-muted',
  positive: 'font-medium text-success',
  warning: 'font-medium text-warning',
  danger: 'font-medium text-danger',
};

export const statusClass: Record<string, string> = {
  pending: 'bg-surface-alt text-muted',
  running: 'bg-accent/10 text-accent-text',
  complete: 'bg-success/10 text-success',
  blocked: 'bg-warning/10 text-warning',
  failed: 'bg-danger/10 text-danger',
};

/** The catalogue `Button` variant of each interaction action style. */
export const actionVariant: Record<string, 'primary' | 'secondary' | 'danger'> = {
  primary: 'primary',
  secondary: 'secondary',
  danger: 'danger',
  warning: 'secondary',
};

export const interactionStatusMeta: Record<
  Exclude<Extract<ConversationMessagePart, { type: 'interaction_request' }>['status'], 'open'>,
  { label: string; detail: string; className: string }
> = {
  submitted: {
    label: 'Submitted',
    detail: 'Your response was sent. The agent can continue from here.',
    className: 'border-success/30 bg-success/10 text-success',
  },
  cancelled: {
    label: 'Canceled',
    detail: 'This request was closed without sending a follow-up response.',
    className: 'border-border bg-surface-alt text-body',
  },
  expired: {
    label: 'Expired',
    detail: 'This request is no longer active.',
    className: 'border-warning/30 bg-warning/10 text-warning',
  },
  superseded: {
    label: 'Replaced',
    detail: 'A newer request replaced this one.',
    className: 'border-border bg-surface-alt text-body',
  },
};

export function renderCell(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

export function defaultFieldValue(field: ConversationInteractionField): unknown {
  if ('defaultValue' in field && field.defaultValue !== undefined) return field.defaultValue;
  if (field.kind === 'multi_choice' || field.kind === 'checkboxes') return [];
  if (field.kind === 'boolean') return false;
  return '';
}

export function buildInteractionDisplayText(
  request: ConversationInteractionRequest,
  values: Record<string, unknown>,
): string {
  return [
    request.title,
    ...request.fields
      .map((field) => {
        const value = values[field.id];
        if (value === undefined || value === null || value === '') return null;
        const options = 'options' in field ? (field.options ?? []) : [];
        const toLabel = (raw: unknown) => {
          const stringValue = String(raw);
          return options.find((option) => option.value === stringValue)?.label ?? stringValue;
        };
        return `${field.label}: ${Array.isArray(value) ? value.map(toLabel).join(', ') : toLabel(value)}`;
      })
      .filter((line): line is string => Boolean(line)),
  ].join('\n');
}

export function isSubmissionActionKind(
  kind: ConversationInteractionAction['kind'],
): kind is ConversationInteractionSubmission['action'] {
  return [
    'submit',
    'cancel',
    'decline',
    'approve',
    'reject',
    'edit',
    'url',
    'create_task',
    'create_project',
    'create_project_thread',
    'create_project_change',
    'start_workflow',
    'apply_proposal',
  ].includes(kind);
}

export function isActionableConversationAction(action: ConversationInteractionAction): boolean {
  return isSubmissionActionKind(action.kind);
}

export function getProgressStatusLabel(status: keyof typeof statusClass): string {
  switch (status) {
    case 'pending':
      return 'Waiting';
    case 'running':
      return 'In progress';
    case 'complete':
      return 'Complete';
    case 'blocked':
      return 'Blocked';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
}

export function getInteractionResolutionMeta(
  status: Exclude<Extract<ConversationMessagePart, { type: 'interaction_request' }>['status'], 'open'>,
) {
  return interactionStatusMeta[status];
}

export function getInteractionActionPendingText(action: ConversationInteractionAction): string {
  switch (action.kind) {
    case 'cancel':
    case 'decline':
    case 'reject':
      return 'Sending decision...';
    case 'start_workflow':
      return 'Queueing next step...';
    case 'create_task':
    case 'create_project':
    case 'create_project_thread':
    case 'create_project_change':
      return 'Creating next step...';
    case 'apply_proposal':
    case 'edit':
      return 'Applying update...';
    default:
      return 'Submitting response...';
  }
}

export function buildInteractionActionDisplayText(
  request: ConversationInteractionRequest,
  values: Record<string, unknown>,
  action: ConversationInteractionAction,
): string {
  if (action.kind === 'submit' || action.kind === 'approve') {
    return buildInteractionDisplayText(request, values) || action.label;
  }
  return action.label;
}

export function prepareInteractionSubmission(input: {
  interactionId?: string;
  sourceMessageId: string;
  request: ConversationInteractionRequest;
  values: Record<string, unknown>;
  action: ConversationInteractionAction;
}): ConversationInteractionSubmission {
  if (!input.interactionId) {
    throw new Error('This request is not ready to submit yet.');
  }
  if (!isSubmissionActionKind(input.action.kind)) {
    throw new Error(`Action "${input.action.label}" is not supported in this client yet.`);
  }
  return validateConversationInteractionSubmission(input.request, {
    interactionId: input.interactionId,
    sourceMessageId: input.sourceMessageId,
    action: input.action.kind,
    values: input.values,
    displayText: buildInteractionActionDisplayText(input.request, input.values, input.action),
    idempotencyKey: `${input.sourceMessageId}:${input.interactionId}:${input.action.id}`,
  });
}
